import { FC } from 'react';
import { useIntl } from 'react-intl';
import { noop } from 'lodash';
import { useSessionStorage } from 'usehooks-ts';
import { faBookmark } from '@fortawesome/pro-duotone-svg-icons';

import {
  RecordsWithTimestamp,
  SAVED_INSTANCES_KEY,
  SAVED_SEARCHES_KEY,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import {
  useFormattedSearches,
  UserSearchDto,
  useSavedInstancesFetch,
  useSavedSearchesFetch,
} from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { ROUTE } from '@app/constants';

import { HistoryRecordsPageWrapper } from '../HistoryRecordsPageWrapper';
import { useBatchInstancesFetch } from '../useBatchInstancesFetch';

export const SavedPage: FC = () => {
  const { isLoggedIn } = useAppContext();
  const { $t } = useIntl();
  const formatSearches = useFormattedSearches();
  const [savedSearches] = useSessionStorage<UserSearchDto[]>(
    SAVED_SEARCHES_KEY,
    []
  );
  const [savedInstances] = useSessionStorage<RecordsWithTimestamp[]>(
    SAVED_INSTANCES_KEY,
    []
  );
  const savedRecordsQuery = useSavedInstancesFetch();
  const savedSearchesQuery = useSavedSearchesFetch();
  const savedRecordsDataQuery = useBatchInstancesFetch({
    queryKey: 'savedRecords',
    list: isLoggedIn ? savedRecordsQuery.data : savedInstances,
  });

  return (
    <HistoryRecordsPageWrapper
      pageTitle={$t(sharedMessages.saved)}
      parentRoute={ROUTE.saved}
      searchesTabConfig={{
        title: $t({
          id: 'saved.searches',
          defaultMessage: 'Searches',
        }),
        documentTitle: $t({
          id: 'saved.savedSearchesPageTitle',
          defaultMessage: 'Searches - Saved - EBSCO Locate',
        }),
        route: ROUTE.savedSearches,
        itemSubtitle: $t(sharedMessages.search),
        list: isLoggedIn
          ? savedSearchesQuery.data
          : formatSearches(savedSearches),
        isError: savedSearchesQuery.isError,
        isLoading: savedSearchesQuery.isInitialLoading,
        refetch: savedSearchesQuery.refetch,
        onEntryRemoval: noop,
        emptyStateMessage: $t({
          id: 'saved.emptyStateSearchMessage',
          defaultMessage: 'You currently have no saved searches.',
        }),
        emptyStateIcon: faBookmark,
        savedSearchesList: isLoggedIn
          ? savedSearchesQuery.data
          : formatSearches(savedSearches),
        isItemRemovable: true,
      }}
      recordsTabConfig={{
        title: $t({
          id: 'saved.records',
          defaultMessage: 'Records',
        }),
        documentTitle: $t({
          id: 'saved.savedRecordsPageTitle',
          defaultMessage: 'Records - Saved - EBSCO Locate',
        }),
        route: ROUTE.savedRecords,
        list: savedRecordsDataQuery.data,
        isError: savedRecordsQuery.isError || savedRecordsDataQuery.isError,
        isLoading:
          savedRecordsQuery.isInitialLoading ||
          savedRecordsDataQuery.isInitialLoading,
        refetch: () =>
          savedRecordsQuery.isError
            ? savedRecordsQuery.refetch()
            : savedRecordsDataQuery.refetch(),
        onEntryRemoval: noop,
        emptyStateMessage: $t({
          id: 'saved.emptyStateRecordMessage',
          defaultMessage: 'You currently have no saved records.',
        }),
        emptyStateIcon: faBookmark,
        isItemRemovable: true,
      }}
      isSearchesTabFirst={false}
    />
  );
};
